import React, { useEffect, useRef, useState } from 'react';
import { forceRefresh, getValidToken, ThumbnailToken } from './token-store';
import { useThumbnailToken } from './use-thumbnail-token';

export interface ThumbnailRetryPolicy {
  /** How many times to retry after a failed load */
  count: number;
  /** Delay before each retry */
  delayMs: number;
}

/** Single immediate retry: assumes the failure was an expired token */
const DEFAULT_RETRY_POLICY: ThumbnailRetryPolicy = { count: 1, delayMs: 0 };

export interface ThumbnailProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  /** File name — the 'f' query param */
  f: string;
  /** Thumbnail size — the 'w' query param */
  w: string | number;
  /** Optional damage id — the 'd' query param */
  d?: string;
  /** Rendered while the token is loading or after a genuine load failure */
  fallback?: React.ReactNode;
  /** Increase count/delayMs for thumbnails that may still be generating server-side */
  retryPolicy?: ThumbnailRetryPolicy;
  onLoadError?: () => void;
}

function buildSrc(token: ThumbnailToken, p: Pick<ThumbnailProps, 'f' | 'w' | 'd'>, attempt: number): string {
  const params = new URLSearchParams({ f: p.f, w: String(p.w) });
  if (p.d) params.set('d', p.d);
  params.set('exp', String(token.exp));
  params.set('scope', token.scope);
  params.set('sig', token.sig);
  if (attempt > 0) params.set('retry', String(attempt)); // cache-bust so the browser refetches
  return `${token.baseUrl}?${params.toString()}`;
}

export function Thumbnail({
  f, w, d, fallback = null, onLoadError, retryPolicy = DEFAULT_RETRY_POLICY, alt = '', ...imgProps
}: ThumbnailProps) {
  const token = useThumbnailToken();
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [freshToken, setFreshToken] = useState<ThumbnailToken | null>(null);
  const mounted = useRef(true);
  const retryTimer = useRef<number>();

  useEffect(() => () => {
    mounted.current = false;
    window.clearTimeout(retryTimer.current);
  }, []);

  // A reused instance pointing at a new thumbnail starts with a clean slate
  useEffect(() => {
    window.clearTimeout(retryTimer.current);
    setAttempt(0);
    setFailed(false);
    setFreshToken(null);
  }, [f, w, d]);

  if (!token || failed) return <>{fallback}</>;

  // Check 2 (reactive): <img> onError can't see the HTTP status, so retry per policy.
  // The first retry force-refreshes the sig (the usual cause is an expired token);
  // later retries reuse it and just refetch (the thumbnail may still be generating).
  const handleError = () => {
    if (attempt >= retryPolicy.count) {
      setFailed(true);
      onLoadError?.();
      return;
    }
    retryTimer.current = window.setTimeout(() => {
      const tokenPromise = attempt === 0 ? forceRefresh() : getValidToken();
      tokenPromise
        .then((t) => {
          if (!mounted.current) return;
          setFreshToken(t);
          setAttempt(attempt + 1);
        })
        .catch(() => {
          if (!mounted.current) return;
          setFailed(true);
          onLoadError?.();
        });
    }, retryPolicy.delayMs);
  };

  const effectiveToken = freshToken ?? token;
  return (
    <img
      {...imgProps}
      alt={alt}
      src={buildSrc(effectiveToken, { f, w, d }, attempt)}
      onError={handleError}
    />
  );
}
